import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import userAPI from "../../api/UserApi";
import { useSiteConfig } from "../../context/SiteConfigContext";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function OrderDetails() {
  const { config } = useSiteConfig();

  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState({});
  const [orderItems, setOrderItems] = useState([]);

  if (!id) {
    navigate("/");
  }

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const res = await userAPI.orderDetails(id);
        if (res.code === 200) {
          setOrder(res.data?.order);
          setOrderItems(res.data?.orderItems);
        }
      } catch (err) {}
    };

    fetchOrderDetails();
  }, [id]);

  const handleDownload = async (orderId) => {
    const element = document.querySelector(".invoice-wrapper");

    // Scroll to top if needed before capturing
    window.scrollTo(0, 0);

    // Wait for a short time to ensure images are fully loaded
    await new Promise((resolve) => setTimeout(resolve, 500));

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true, // 👈 Important for external images
      allowTaint: false,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pageWidth;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    // Fit to one page (or multiple if needed)
    if (pdfHeight > pageHeight) {
      let position = 0;
      while (position < pdfHeight) {
        pdf.addImage(imgData, "PNG", 0, -position, pdfWidth, pdfHeight);
        position += pageHeight;
        if (position < pdfHeight) pdf.addPage();
      }
    } else {
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    }

    pdf.save(orderId + ".pdf");
  };

  return (
    <>
      <div className="my-acount-content account-address mb-5">
        <div className="row">
          <div className="col-md-12 text-end">
            <button
              className="btn"
              onClick={(e) => handleDownload(order?.orderId)}
            >
              Download Invoice
            </button>
          </div>
        </div>

        <div className="invoice-wrapper mb-5">
          <div className="invoice-box">
            <div className="invoice-header d-flex justify-content-between align-items-center flex-wrap">
              <div className="company-info mb-3 mb-md-0 text-center text-md-start pb-4">
                <img
                  src={config?.logo}
                  className="mb-2"
                  alt="Logo"
                  style={{ maxWidth: 100 }}
                />
                <h4 className="mb-1">{config?.name}</h4>
                <p className="mb-0">
                  <a href={`mailto: ${config?.email}`} className="text-primary">
                    {config?.email}
                  </a>
                </p>
                <p className="mb-0">
                  <a href={`tel: ${config?.phone}`} className="text-primary">
                    +91-{config?.phone}
                  </a>
                </p>
              </div>

              <div className="invoice-meta text-center text-md-end">
                <h5 className="mb-1">OrderId: #{order?.orderId}</h5>
                <p className="mb-0">{order?.orderDate}</p>
              </div>
            </div>

            <div className="client-info bg-light mb-3">
              <h6 className="fw-bold mb-1">Bill To:</h6>
              <p className="mb-0">
                {order?.address?.firstName} {order?.address?.lastName}
                <br />
                {order?.address?.addressLine1}{" "}
                {order?.address?.addressLine2
                  ? order?.address?.addressLine2 + ","
                  : ""}
                <br />
                {order?.address?.district}, {order?.address?.state} -
                {order?.address?.pinCode} {order?.address?.country}
                <br />
                {order.email ? order.email + "," : ""} {order?.phone}
              </p>
            </div>

            <div className="table-responsive mb-4">
              <table className="table table-bordered text-center align-middle shadow-sm">
                <thead className="table-dark">
                  <tr>
                    <th>Item</th>
                    <th>Price</th>
                    <th>Qty</th>
                    <th>Total</th>
                  </tr>
                </thead>
                {orderItems && (
                  <tbody>
                    {orderItems.map((orderItem, index) => (
                      <tr>
                        <td className="text-start">
                          <div className="d-flex flex-column flex-md-row align-items-md-center">
                            <img
                              src={orderItem.productId.thumbnail}
                              style={{ width: 60, height: "auto" }}
                              alt="Product"
                              className="me-md-2 mb-2 mb-md-0"
                            />
                            <div>
                              <strong>{orderItem.productName}</strong>
                              <br />
                              <small>{orderItem.variantName}</small>
                            </div>
                          </div>
                        </td>
                        <td>₹{orderItem.price.toFixed(2)}/-</td>
                        <td>{orderItem.quantity}</td>
                        <td className="text-end">
                          ₹{(orderItem.price * orderItem.quantity).toFixed(2)}/-
                        </td>
                      </tr>
                    ))}

                    <tr>
                      <td colSpan={3} className="text-end fw-bold">
                        SubTotal:
                      </td>
                      <td className="fw-bold text-end">₹{order.subtotal}/-</td>
                    </tr>

                    {order.discount > 0 && (
                      <tr>
                        <td colSpan={3} className="text-end fw-bold">
                          Discount:
                        </td>
                        <td className="fw-bold text-end text-success">
                          (-) ₹{order.discount}/-
                        </td>
                      </tr>
                    )}

                    {order.shipping > 0 && (
                      <tr>
                        <td colSpan={3} className="text-end fw-bold">
                          Shipping:
                        </td>
                        <td className="fw-bold text-end text-success">
                          ₹{order.shipping}/-
                        </td>
                      </tr>
                    )}

                    <tr>
                      <td colSpan={3} className="text-end fw-bold">
                        Total:
                      </td>
                      <td className="fw-bold text-end">₹{order.total}/-</td>
                    </tr>
                  </tbody>
                )}
              </table>
            </div>
            <div className="text-center small text-muted mb-3">
              Thank you for your purchase!
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default OrderDetails;
